import { ApiResult, HttpClient } from "../http.js";
import {
  BlueprintAnalysisDto,
  BlueprintAnalysisListItemDto,
  BlueprintChatMessageDto,
  UpdateTakeoffItemRequest,
} from "../types/blueprints.js";

export interface UploadBlueprintPayload {
  /** FormData pre-populated with `file`, `projectId` and optional `title`. */
  formData: FormData;
}

export class BlueprintsApi {
  constructor(private readonly http: HttpClient) {}

  list(projectId: string): Promise<ApiResult<BlueprintAnalysisListItemDto[]>> {
    return this.http.get<BlueprintAnalysisListItemDto[]>("/api/blueprint-analyses", {
      query: { projectId },
    });
  }

  listAll(): Promise<ApiResult<BlueprintAnalysisListItemDto[]>> {
    return this.http.get<BlueprintAnalysisListItemDto[]>("/api/analysis-overview/blueprints");
  }

  listStandalone(): Promise<ApiResult<BlueprintAnalysisListItemDto[]>> {
    return this.http.get<BlueprintAnalysisListItemDto[]>("/api/blueprint-analyses", {
      query: { scope: "standalone" },
    });
  }

  get(id: string): Promise<ApiResult<BlueprintAnalysisDto>> {
    return this.http.get<BlueprintAnalysisDto>(`/api/blueprint-analyses/${encodeURIComponent(id)}`);
  }

  upload(payload: UploadBlueprintPayload): Promise<ApiResult<BlueprintAnalysisDto>> {
    return this.http.post<BlueprintAnalysisDto>("/api/blueprint-analyses", { body: payload.formData });
  }

  remove(id: string): Promise<ApiResult<string>> {
    return this.http.delete<string>(`/api/blueprint-analyses/${encodeURIComponent(id)}`);
  }

  retry(id: string): Promise<ApiResult<BlueprintAnalysisDto>> {
    return this.http.post<BlueprintAnalysisDto>(`/api/blueprint-analyses/${encodeURIComponent(id)}/retry`);
  }

  updateTakeoffItem(
    analysisId: string,
    itemId: string,
    body: UpdateTakeoffItemRequest,
  ): Promise<ApiResult<BlueprintAnalysisDto>> {
    return this.http.put<BlueprintAnalysisDto>(
      `/api/blueprint-analyses/${encodeURIComponent(analysisId)}/takeoff-items/${encodeURIComponent(itemId)}`,
      { body },
    );
  }

  chatHistory(id: string): Promise<ApiResult<BlueprintChatMessageDto[]>> {
    return this.http.get<BlueprintChatMessageDto[]>(`/api/blueprint-analyses/${encodeURIComponent(id)}/chat`);
  }

  ask(id: string, question: string): Promise<ApiResult<BlueprintChatMessageDto[]>> {
    return this.http.post<BlueprintChatMessageDto[]>(`/api/blueprint-analyses/${encodeURIComponent(id)}/chat`, {
      body: { question },
    });
  }

  createCalculation(id: string): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/blueprint-analyses/${encodeURIComponent(id)}/create-calculation`);
  }

  /**
   * Pass no quoteNumber to have the server allocate from the org's sequence.
   */
  createQuote(id: string, quoteNumber?: string | null): Promise<ApiResult<string>> {
    return this.http.post<string>(`/api/blueprint-analyses/${encodeURIComponent(id)}/create-quote`, {
      body: { quoteNumber },
    });
  }
}
